import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";

// Actions
import { resetTgGroup } from "../../../actions/org/OrgTargetGroupActions";
import { getRegionNames } from "../../../actions/regions/RegionActions";
import { resetCityRoadStretches } from "../../actions/campaign-planning/CampaignPlanningRoadStretchesActions";
import { resetTgSpecificOts } from "../../actions/campaign-planning/TgSpecificOtsActions";
import {
  getRegionsData,
  resetCampaignPlanning,
} from "../../actions/campaign-planning/CampaignPlanningActions";
import { resetCampaignDetails } from "../../../actions/campaign/CampaignBasicInfoActions";
import { resetCampaignPlanDetails } from "../../../actions/campaign-plan/CampaignPlanActions";
import { removeTgHeatMapData } from "../../../actions/geo-data/GeoDataActions";
import { clearSelectedRegionAndZonesMap } from "../../actions/campaign-planning/ZonesAndSubZonesActions";
import {
  resetBrandInfo,
  resetPoiInfluenceCircleRadius,
} from "../../../actions/map-view/poi/PoiSelectionActions";
import { clearRouteTypesSelection } from "../../../actions/org/OrgRouteTypesActions";
import { clearCustomDataLayers } from "../../../actions/org/OrgDataLayerActions";
import { resetDataLayerInfo } from "../../../actions/map-view/DataLayerSelectionActions";
import { clearCityConfig } from "../../../actions/org/OrgCityConfigActions";

// Constants and Utils
import {
  useGetBasicCampaignDetails,
  useGetCampaignPlanOverview,
  useGetDataLayers,
  useGetMediaTypesAndTouchPoints,
  useRestoreCampaignPlan,
} from "../../utils/HooksUtil";
import {
  useGetPoiHierarchy,
  useGetRegionListByType,
  useGetRouteTypes,
  useGetTargetGroups,
  useGetTgAttributes,
} from "../../../mavin/utils/hooks/HooksUtil";
import { useSortableData } from "./SortingUtils";
import {
  getCampaignExistingCitiesMap,
  getPlanningQueryParams,
} from "./CampaignPlanningUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import SingleCitySelectionForm from "../../../mavin/components/single-city-selection-form/SingleCitySelectionForm";

// Page Sections
import PlanningMapView from "./PlanningMapView";
import PlanningFooterSection from "./PlanningFooterSection";
import PlanningHeaderSection from "./PlanningHeaderSection";
import RoadStretchTable from "./RoadStretchTable";
import CostAndOtsSection from "./CostAndOtsSection";
import { PoiSelectionForm } from "./PoiSelectionForm";
import PlanningMapTabsSection from "./PlanningMapTabsSection";
import TabContentPreviewForm from "./TabContentPreviewForm";

// get Road Stretches BaseOts
function getRoadStretchesBaseOts(stretchesMap = {}) {
  const isStretchesPresent = Object.keys(stretchesMap).length > 0;

  let baseOts = 0;

  if (!isStretchesPresent) {
    return baseOts;
  }

  Object.keys(stretchesMap).forEach((stretchId) => {
    const stretchInfo = stretchesMap[stretchId] || {};
    const {
      // originalOtsAvg,
      //  originalOtsLitAvg,
      otsAvg,
      otsLitAvg,
    } = stretchInfo || {};
    // const impressions = originalOtsLitAvg ?? originalOtsAvg;
    const impressions = otsLitAvg ?? otsAvg;
    baseOts = impressions > baseOts ? impressions : baseOts;
  });

  return baseOts;
}

/**
 * Page
 */
export default function CampaignPlanningPage() {
  const dispatch = useDispatch();
  const { campaignId } = useParams();
  const { search } = useLocation();
  const { queryCityId, isPureDooh, isAddCity } = getPlanningQueryParams(search);

  // State
  const [searchedStretches, setSearchedStretches] = useState([]);

  // for "SINGLE City-Selection-form"
  const openSingleCitySelectionForm = useSelector(
    (state) => state.singleCitySelectionModal.openModal
  );

  // For Poi-Selection-Form Model
  const openPoiSelectionForm = useSelector(
    (state) => state.poiSelectionForm.openModal
  );

  const isTabContentPreviewFormVisible = useSelector(
    (state) => state.tabContentPreviewForm.openModal
  );

  // for "region-data"
  const regionsDataLoading = useSelector(
    (state) => state.campaignPlanning.regionLoading
  );
  const regionsData = useSelector(
    (state) => state.campaignPlanning.regionsData
  );
  const { bbox: cityBBox } = regionsData || {};

  // filtered stretches map
  const filteredStretchesMap = useSelector(
    (state) => state.planningRoadStretches.filteredStretchesMap
  );

  // TgSpecific Ots
  const tgSpecificOts =
    useSelector((state) => state.tgSpecificOts.tgSpecificOts) || {};

  // selected touch points filtered stretches
  const touchPointsFilteredStretchesMap = useSelector(
    (state) => state.planningRoadStretches.touchPointsFilteredStretchesMap
  );

  // selected FreqOptLoc filtered stretches
  const folFilteredStretchesMap = useSelector(
    (state) => state.planningRoadStretches.folFilteredStretchesMap
  );

  // overall Filtered Stretches Map (other filters + touch points + freqOptLoc)
  // keep the same order :: fol, touchPoint, other
  const overallFilteredStretchesMap = {
    ...folFilteredStretchesMap,
    ...touchPointsFilteredStretchesMap,
    ...filteredStretchesMap,
  };

  // Base Impressions for progress bars (We use the highest impressions as the base)
  // const baseOts = useSelector((state) => state.planningRoadStretches.baseOts);
  const baseOts = getRoadStretchesBaseOts(overallFilteredStretchesMap);

  // "road-stretches" with tgSpecific Ots
  const newRoadStretchesData = Object.values(overallFilteredStretchesMap).map(
    (item) => ({
      ...item,
      ...tgSpecificOts[item.id],
    })
  );

  // road-stretches sorting details
  const [sortedRoadStretchesData, requestRoadStretchSort] =
    useSortableData(searchedStretches);

  const roadStretchLoading = useSelector(
    (state) => state.planningRoadStretches.loading
  );

  // regions name list
  const regionsName = useSelector((state) => state.region.regionsName) || [];
  const regionNamesLoading = useSelector((state) => state.region.namesLoading);

  // TG list Loading
  const tgListLoading = useSelector(
    (state) => state.orgTargetGroup.targetGroupLoading
  );

  // Get CityId once city is selected
  const cityId = useSelector((state) => state.campaignPlanning.cityId);

  // plan overview loading
  const campaignPlanOverviewLoading = useSelector(
    (state) => state.campaignPlan.campaignPlanOverviewLoading
  );

  // basic campaign (for add city)
  const campaign = useSelector((state) => state.c_campaignBasicInfo.campaign);
  const basicCampaignLoading = useSelector(
    (state) => state.c_campaignBasicInfo.campaignLoading
  );
  const existingCitiesMap = getCampaignExistingCitiesMap(campaign, isAddCity);

  // Dispatch
  useEffect(() => {
    // Reset Actions
    dispatch(resetCityRoadStretches());
    dispatch(resetTgSpecificOts());
    dispatch(resetCampaignPlanning());
    dispatch(resetTgGroup());
    dispatch(resetCampaignDetails());
    dispatch(resetCampaignPlanDetails());
    dispatch(removeTgHeatMapData());
    dispatch(clearSelectedRegionAndZonesMap());
    dispatch(resetBrandInfo());
    dispatch(resetPoiInfluenceCircleRadius());
    dispatch(clearRouteTypesSelection());
    dispatch(clearCustomDataLayers());
    dispatch(resetDataLayerInfo());
    dispatch(clearCityConfig());

    // get regions
    dispatch(getRegionNames());
  }, [dispatch]);

  // Custom useEffect
  useGetTargetGroups(cityId);
  useRestoreCampaignPlan(campaignId, queryCityId);
  useGetPoiHierarchy(cityId);
  useGetTgAttributes(cityId);
  useGetRegionListByType(cityId);
  useGetRouteTypes(cityId);
  useGetDataLayers(cityId);
  useGetMediaTypesAndTouchPoints(cityId);

  // hooks for "add city"
  useGetBasicCampaignDetails(isAddCity, campaignId);
  useGetCampaignPlanOverview(isAddCity, campaignId);

  // Functions
  function onSubmitCitySelectionForm(cityId) {
    dispatch(resetCityRoadStretches());
    dispatch(getRegionsData(cityId, isPureDooh));
  }

  // page loading
  if (
    regionNamesLoading ||
    tgListLoading ||
    basicCampaignLoading ||
    campaignPlanOverviewLoading
  ) {
    return <Spinner className="spinner-center mt-2" />;
  }

  return (
    <>
      <div className="campaign-planning">
        <div className="planning-layout-left">
          {/* Header Section */}
          <PlanningHeaderSection
            regionsData={regionsData}
            newRoadStretchesData={newRoadStretchesData}
            setSearchedStretches={setSearchedStretches}
            isAddCity={isAddCity}
          />

          {/* Road Stretch Container */}
          <div className="planning-road-container pb-4">
            <RoadStretchTable
              roadStretchData={Object.values(sortedRoadStretchesData)}
              baseOts={baseOts}
              roadStretchLoading={roadStretchLoading}
              regionsDataLoading={regionsDataLoading}
              requestRoadStretchSort={requestRoadStretchSort}
            />
          </div>

          {/* Footer Section */}
          <div className="fixed-bottom px-0 bg-white w-47">
            <CostAndOtsSection />
            <PlanningFooterSection
              isAddCity={isAddCity}
              isPureDooh={isPureDooh}
            />
          </div>
        </div>

        {/* map view */}
        <div className="planning-layout-right bg-alt">
          <PlanningMapView
            roadStretchData={Object.values(newRoadStretchesData)}
            sortedRoadStretchesData={Object.values(sortedRoadStretchesData)}
          />

          <PlanningMapTabsSection cityId={cityId} cityBBox={cityBBox} />
        </div>
      </div>

      {/* CitySelection form */}
      {openSingleCitySelectionForm && (
        <SingleCitySelectionForm
          regionsName={regionsName}
          selectedCitiesMap={existingCitiesMap}
          onSubmit={onSubmitCitySelectionForm}
          selectedCityId={cityId}
        />
      )}

      {/* Poi-Selection form */}
      {openPoiSelectionForm && <PoiSelectionForm cityId={cityId} />}

      {/* Selected Content Preview Form */}
      {isTabContentPreviewFormVisible && <TabContentPreviewForm />}
    </>
  );
}
